import React from 'react';
import PageLayout from '../components/PageLayout';
import Seo from '../components/Seo';
import { useState } from 'react';
import { useForm } from '@formspree/react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Link } from 'react-router-dom';
import { Headset, Ticket, Warning, Phone } from 'phosphor-react';

const HelpDesk = () => {
  const [formState, handleSubmit] = useForm(process.env.REACT_APP_HELPDESK_FORMSPREE_ID);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    urgency: '',
    message: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
  const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      let formatted = '';
      if (digitsOnly.length > 0) {
        formatted = `(${digitsOnly.slice(0, 3)}`;
        if (digitsOnly.length > 3) formatted += `) ${digitsOnly.slice(3, 6)}`;
        if (digitsOnly.length > 6) formatted += `-${digitsOnly.slice(6, 10)}`;
      }
      setFormData(prev => ({ ...prev, phone: formatted }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
  const errors = {};
    if (!formData.urgency) errors.urgency = 'Please select an urgency level';
    if (formData.phone && !/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit US phone number';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleFormSubmit = (e) => {
    if (!captchaToken) {
      e.preventDefault();
      setCaptchaError('Please complete the CAPTCHA verification');
      return;
    }
    if (!validate()) {
      e.preventDefault();
      return;
    }
    handleSubmit(e);
  };

  if (formState.succeeded) {
    return (
        <div className="text-center py-16">
          <div className="text-green-600 text-5xl mb-4">✓</div>
          <h3 className="text-2xl font-bold text-terrapin-blue mb-3">Request Submitted!</h3>
          <p>A member of our team will be in touch shortly.</p>
        </div>
    );
  }
  return (
      <>
      <Seo
        title="IT Support & Helpdesk | Terrapin Networks"
        description="Need IT help fast? Terrapin Networks offers responsive helpdesk and emergency IT support for small businesses in Northern Michigan."
      />
      <PageLayout
        title="Help Desk"
        description={
          <div className="max-w-3xl">
            <p className="text-lg">
              Please use the following contact information for technical support requests only.
            </p>
            <p className="text-lg mt-2">
              For all other inquiries please use the information and form on the{' '}
              <Link to="/contact" className="underline hover:text-terrapin-red transition-colors">
                Contact Us
              </Link>{' '}
              page.
            </p>
          </div>
        }
        headerStyle="default"
        contentWidth="default"
        contentClassName="bg-white rounded-lg shadow-sm p-8"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Phone Support Section */}
            <section>
              <div className="flex gap-6">
                {/* Image Column */}
                <div className="hidden md:block w-1/3 flex-shrink-0">
                  <img
                    src={require('../assets/support.jpg')}
                    alt="Terrapin Networks support team"
                    className="w-full h-full object-cover rounded-lg"
                    loading="lazy"
                  />
                  </div>
                {/* Phone Support Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-6">
                    <Headset size={32} weight="regular" className="text-terrapin-blue" />
                    <h2 className="text-2xl font-bold text-gray-800">Phone Support</h2>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-2">BUSINESS HOURS</h3>
                      <p className="text-gray-600">Monday – Friday | 8:30 AM – 5:00 PM ET</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-700 mb-2">Phone:</h3>
                      <a
                        href="tel:231-941-2100"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-terrapin-red text-white rounded-lg hover:bg-opacity-90 transition-all duration-200"
                      >
                        <Phone size={20} weight="fill" className="flex-shrink-0" />
                        <span className="font-medium">231-941-2100</span>
                      </a>
                    </div>

                  </div>
                </div>
              </div>
              <div className="bg-terrapin-blue bg-opacity-5 p-6 rounded-lg mt-6 border-l-4 border-terrapin-blue">
                <div className="flex gap-3">
                  <Warning size={24} weight="fill" className="text-terrapin-blue flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-800 mb-1">Emergency Support Available 24/7</h4>
                    <p className="text-gray-600">
                      For emergency support outside of business hours, call 231-941-2100 and press "9".
                      This is monitored by an operator 24/7. A Network Engineer is on call and will respond promptly.
                    </p>
                  </div>
                </div>
              </div>
            </section>
            {/* Service Request Form Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <Ticket size={32} weight="regular" className="text-terrapin-blue" />
                <h2 className="text-2xl font-bold text-gray-800">Send a Service Request to our Help Desk</h2>
              </div>
              <form className="space-y-6" onSubmit={handleFormSubmit}>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-terrapin-blue focus:ring-2 focus:ring-terrapin-blue focus:ring-opacity-20 transition-colors"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company Name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-terrapin-blue focus:ring-2 focus:ring-terrapin-blue focus:ring-opacity-20 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-terrapin-blue focus:ring-2 focus:ring-terrapin-blue focus:ring-opacity-20 transition-colors"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Best Number To Reach You"
                    className={`w-full px-4 py-3 rounded-lg border ${validationErrors.phone ? 'border-red-500' : 'border-gray-300'} focus:border-terrapin-blue focus:ring-2 focus:ring-terrapin-blue focus:ring-opacity-20 transition-colors`}
                  />
                  {validationErrors.phone && <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>}
                  <p className="text-gray-500 text-xs mt-1">Format: (xxx) xxx-xxxx</p>
                </div>
                <div>
                  <select
                    name="urgency"
                    value={formData.urgency}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border ${validationErrors.urgency ? 'border-red-500' : 'border-gray-300'} focus:border-terrapin-blue focus:ring-2 focus:ring-terrapin-blue focus:ring-opacity-20 transition-colors`}
                  >
                    <option value="" disabled>Urgency (Please choose one)</option>
                    <option value="high">High - System/Network Down</option>
                    <option value="medium">Medium - Significant Impact</option>
                    <option value="low">Low - Minor Issue</option>
                  </select>
                  {validationErrors.urgency && <p className="text-red-500 text-sm mt-1">{validationErrors.urgency}</p>}
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Message"
                    rows={6}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-terrapin-blue focus:ring-2 focus:ring-terrapin-blue focus:ring-opacity-20 transition-colors"
                    required
                  />
                </div>
                <div className="mt-2">
                  <HCaptcha
                    sitekey={process.env.REACT_APP_HCAPTCHA_SITE_KEY}
                    onVerify={handleCaptchaVerify}
                  />
                  {captchaError && <p className="text-red-500 text-sm mt-1">{captchaError}</p>}
                </div>
                <button
                  type="submit"
                  disabled={formState.submitting || !captchaToken}
                  className="w-full bg-terrapin-red text-white py-4 px-8 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-200 disabled:opacity-50"
                >
                  {formState.submitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
              
            </section>
          </div>
        </div>
      </PageLayout>
      </>
    );
  }

export default HelpDesk; 